import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import GrIconTile from "./GrIconTile";
import GrIcon from "./GrIcon";
import icons from "../icons";
import { gr } from "../tokens";

const PHONE = "@media (max-width: 599.95px)";

// iconPlain: the icon alone in the tone colour (a status mark), no tile.
const PLAIN_TONES = {
  brand: gr.color.brand,
  neutral: gr.color.muted,
  ok: gr.color.ok,
  overdue: gr.color.overdue,
  late: gr.color.late,
};

// List row: icon tile, title, meta line, optional trailing pill/badge, optional action.
// Rows placed next to each other get a hairline between them. On phones the action drops under the text.
// `iconPlain` shows a small status icon instead of the tile (e.g. a check-in list: done ✓ / missed ○).
// `compact`: one line at every width (title cut with "…", action stays on the right), 56px tall —
// for short summary lists inside a card (dashboard journeys).
// `onClick` opens the item through one transparent button over the whole row (`openLabel` names it);
// the action sits above that button, so the two are never nested. `chevron` (no action) shows a "›"
// at the right edge of an opening row.
const OPEN_CLASS = "gr-list-row-open";
const ellipsisSx = { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

export default function GrListRow({
  icon,
  iconTone = "brand",
  iconPlain = false,
  title,
  meta,
  trailing,
  action,
  compact = false,
  chevron = false,
  onClick,
  openLabel,
  as = "div",
  sx,
}) {
  const iconSpace = iconPlain
    ? `calc(${gr.icon.lg} + ${gr.space[3]})`
    : "56px";
  return (
    <Box
      component={as}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: iconPlain ? gr.space[3] : gr.space[4],
        py: gr.space[3],
        listStyle: "none",
        "& + &": { borderTop: `1px solid ${gr.color.line2}` },
        [PHONE]: { flexWrap: "wrap", rowGap: gr.space[3] },
        ...(compact && {
          gap: gr.space[3],
          py: gr.space[2],
          minHeight: `calc(${gr.control.lg} + ${gr.space[2]})`,
          [PHONE]: { flexWrap: "nowrap" },
        }),
        ...(onClick && {
          position: "relative",
          // Hover tint reaches a little past the text, like other clickable rows.
          px: gr.space[3],
          mx: `calc(-1 * ${gr.space[3]})`,
          borderRadius: gr.radius.tile,
          transition: "background-color .15s ease",
          "&:hover": { background: gr.color.tint },
          [`&:has(> .${OPEN_CLASS}:focus-visible)`]: { boxShadow: gr.shadow.focus },
        }),
        ...sx,
      }}
    >
      {onClick ? (
        <Box
          component="button"
          type="button"
          className={OPEN_CLASS}
          aria-label={openLabel}
          onClick={onClick}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 1, // over the text, so a click anywhere on the row opens the item
            p: 0,
            border: 0,
            background: "transparent",
            borderRadius: "inherit",
            cursor: "pointer",
            "&:focus-visible": { outline: "none" },
          }}
        />
      ) : null}
      {icon && iconPlain ? (
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: "inline-flex",
            flex: "none",
            color: PLAIN_TONES[iconTone] || PLAIN_TONES.brand,
          }}
        >
          {icon}
        </Box>
      ) : icon ? (
        <GrIconTile size="sm" tone={iconTone}>
          {icon}
        </GrIconTile>
      ) : null}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "2px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: gr.space[2],
            flexWrap: compact ? "nowrap" : "wrap",
          }}
        >
          <Box
            component="span"
            sx={{
              fontSize: gr.font.size.md,
              fontWeight: gr.font.weight.semibold,
              color: gr.color.ink,
              ...(compact && { ...ellipsisSx, minWidth: 0 }),
            }}
          >
            {title}
          </Box>
          {compact && trailing ? (
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: gr.space[2], flex: "none" }}
            >
              {trailing}
            </Box>
          ) : (
            trailing
          )}
        </Box>
        {meta ? (
          // A block, so a meta can hold more than one line (e.g. a progress bar over a date line).
          <Box
            sx={{
              fontSize: gr.font.size.sm,
              color: gr.color.ink2,
              ...(compact && ellipsisSx),
            }}
          >
            {meta}
          </Box>
        ) : null}
      </Box>
      {action ? (
        <Box
          sx={{
            flex: "none",
            // Above the row's open button, so the action gets its own clicks.
            ...(onClick && { position: "relative", zIndex: 2 }),
            ...(!compact && {
              [PHONE]: {
                flexBasis: icon ? `calc(100% - ${iconSpace})` : "100%",
                ml: icon ? iconSpace : 0,
                "& > *": { width: "100%" },
              },
            }),
          }}
        >
          {action}
        </Box>
      ) : chevron ? (
        <Box
          component="span"
          aria-hidden="true"
          sx={{ display: "inline-flex", flex: "none", color: gr.color.muted }}
        >
          <GrIcon icon={icons.chevronRight} />
        </Box>
      ) : null}
    </Box>
  );
}

GrListRow.propTypes = {
  icon: PropTypes.node,
  iconTone: PropTypes.oneOf(["brand", "neutral", "ok", "late", "overdue"]),
  iconPlain: PropTypes.bool,
  title: PropTypes.node.isRequired,
  meta: PropTypes.node,
  trailing: PropTypes.node,
  action: PropTypes.node,
  compact: PropTypes.bool,
  chevron: PropTypes.bool,
  onClick: PropTypes.func,
  openLabel: PropTypes.string,
  as: PropTypes.string,
  sx: PropTypes.object,
};
